import type { TCreatePost, TError, TPostBase, TPostType } from '@onesource/schemas';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios from '../../axios';
import { invalidateMyAnalytics } from '../my-analytics/get-my-analytics';
import { getAvailableTagsBaseQueryKey } from '../tags/get-available-tags';
import { getPostsQueryKey } from './get-posts';
import { getPostsInfiniteQueryKey } from './get-posts-infinite';
import { getScheduledPostsQueryKey } from './get-scheduled-posts';
import { getSpacePostTypesQueryKey } from './get-space-post-types';

/**
 * Hook to create a new post in a space
 * Automatically invalidates both regular and infinite posts list for the space on success
 *
 * @param spaceId - Space ID (UUID) where the post will be created
 */
export const useCreatePost = (spaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation<TPostBase, TError, TCreatePost>({
    mutationFn: async data => {
      const res = await axios.post<TPostBase>(`/community-hub/spaces/${spaceId}/posts`, data);
      return res.data;
    },
    onSuccess: created => {
      // Invalidate both regular and infinite posts list to trigger refetch
      queryClient.invalidateQueries({ queryKey: getPostsQueryKey(spaceId) });
      queryClient.invalidateQueries({ queryKey: getPostsInfiniteQueryKey(spaceId) });
      // A future-scheduled post lands in the Scheduled view, not the feed.
      queryClient.invalidateQueries({ queryKey: getScheduledPostsQueryKey(spaceId) });
      // The post's tags may now be "used", so the feed filter's tag list is stale.
      queryClient.invalidateQueries({ queryKey: getAvailableTagsBaseQueryKey(spaceId) });
      // Refresh the "Post type" filter's used-types list ONLY when this post
      // introduces a type not already in the cached set — a post of an
      // already-present type can't change the list, so skip the refetch. When
      // the list was never fetched (undefined) there's nothing on screen to
      // update; it loads fresh next time the filter opens.
      const postTypesKey = getSpacePostTypesQueryKey(spaceId);
      const cachedTypes = queryClient.getQueryData<TPostType[]>(postTypesKey);
      if (created.postType && cachedTypes && !cachedTypes.includes(created.postType)) {
        queryClient.invalidateQueries({ queryKey: postTypesKey });
      }
      // A new post feeds the member's activity analytics.
      invalidateMyAnalytics(queryClient);
    },
  });
};
